// src/components/settings/LinkedInOnboardingWizard.jsx - ENHANCED VERSION with Company Setup
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
    CheckCircleIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    ClipboardDocumentIcon,
    LinkIcon,
    ArrowRightIcon,
    ArrowLeftIcon,
    EyeIcon,
    EyeSlashIcon,
    ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const STEPS = {
    WELCOME: 'welcome',
    CREATE_APP: 'create_app',
    APP_CREDENTIALS: 'app_credentials',
    OAUTH_FLOW: 'oauth_flow',
    VERIFICATION: 'verification',
    COMPANY_SETUP_CHOICE: 'company_setup_choice',
    ADVERTISING_API_REQUEST: 'advertising_api_request',
    COMPANY_OAUTH: 'company_oauth',
    MANUAL_URN_ENTRY: 'manual_urn_entry',
    COMPANY_VERIFICATION: 'company_verification',
    COMPLETE: 'complete'
};

function LinkedInOnboardingWizard({ onComplete, onClose }) {
    const [currentStep, setCurrentStep] = useState(STEPS.WELCOME);
    const [appCredentials, setAppCredentials] = useState({
        clientId: '',
        clientSecret: ''
    });
    const [showClientSecret, setShowClientSecret] = useState(false);
    const [oauthInProgress, setOauthInProgress] = useState(false);
    const [credentialsSaved, setCredentialsSaved] = useState(false);
    const [companyOauthInProgress, setCompanyOauthInProgress] = useState(false);
    const [wantsCompanySetup, setWantsCompanySetup] = useState(null);
    const [advertisingApiRequested, setAdvertisingApiRequested] = useState(false);
    const [manualOrganizationUrn, setManualOrganizationUrn] = useState('');
    const queryClient = useQueryClient();

    // Check current LinkedIn setup status
    const { data: linkedinStatus, refetch: refetchStatus, isLoading: statusLoading } = useQuery(
        'linkedinSetupStatus',
        () => fetch('/api/settings/linkedin/status', {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            }
        }).then(res => res.json()),
        {
            retry: 1,
            refetchOnWindowFocus: false,
            refetchInterval: 5000 // Refetch every 5 seconds to track progress
        }
    );

    // Save manual organization URN mutation
    const saveManualUrnMutation = useMutation(
        (organizationUrn) => {
            return fetch('/api/settings/linkedin/save-organization-urn', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ organizationUrn })
            }).then(res => {
                if (!res.ok) {
                    return res.json().then(errorData => {
                        throw new Error(errorData.message || `HTTP ${res.status}`);
                    });
                }
                return res.json();
            });
        },
        {
            onSuccess: (data) => {
                toast.success('Organization URN saved successfully!');
                // Refetch status to confirm save
                setTimeout(() => {
                    refetchStatus().then(result => {
                        if (result.data?.data?.hasOrganizationUrn) {
                            setCurrentStep(STEPS.COMPANY_VERIFICATION);
                        }
                    });
                }, 1000);
            },
            onError: (error) => {
                console.error('❌ Failed to save organization URN:', error);
                toast.error(`Failed to save URN: ${error.message}`);
            }
        }
    );

    // ENHANCED: Auto-advance steps based on current status including company setup
    useEffect(() => {
        if (linkedinStatus?.success && linkedinStatus?.data) {
            const status = linkedinStatus.data;

            // Auto-advance based on what's already configured
            if (status.isFullyConfigured && status.hasOrganizationUrn) {
                setCurrentStep(STEPS.COMPLETE);
            } else if (status.hasOrganizationUrn) {
                setCurrentStep(STEPS.COMPANY_VERIFICATION);
            } else if (status.hasOrganizationToken && !status.hasOrganizationUrn) {
                // Has token but no URN - likely need manual entry
                setCurrentStep(STEPS.MANUAL_URN_ENTRY);
            } else if (status.hasPersonalUrn && wantsCompanySetup === true) {
                setCurrentStep(STEPS.ADVERTISING_API_REQUEST);
            } else if (status.hasPersonalUrn && wantsCompanySetup === null) {
                setCurrentStep(STEPS.COMPANY_SETUP_CHOICE);
            } else if (status.hasPersonalUrn && wantsCompanySetup === false) {
                setCurrentStep(STEPS.COMPLETE);
            } else if (status.hasPersonalToken) {
                setCurrentStep(STEPS.VERIFICATION);
            } else if (status.hasAppCredentials) {
                setCurrentStep(STEPS.OAUTH_FLOW);
                setCredentialsSaved(true);
            }
        }
    }, [linkedinStatus, wantsCompanySetup]);

    // Save app credentials mutation
    const saveAppCredentialsMutation = useMutation(
        (credentials) => {
            return fetch('/api/settings/linkedin/app-credentials', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(credentials)
            }).then(res => {
                if (!res.ok) {
                    return res.json().then(errorData => {
                        throw new Error(errorData.message || `HTTP ${res.status}`);
                    });
                }
                return res.json();
            });
        },
        {
            onSuccess: (data) => {
                toast.success('LinkedIn app credentials saved!');
                setCredentialsSaved(true);

                // Refetch status to confirm save
                setTimeout(() => {
                    refetchStatus().then((result) => {
                        if (result.data?.data?.hasAppCredentials) {
                            setCurrentStep(STEPS.OAUTH_FLOW);
                        }
                    });
                }, 1000);
            },
            onError: (error) => {
                console.error('❌ Failed to save LinkedIn app credentials:', error);
                toast.error(`Failed to save credentials: ${error.message}`);
                setCredentialsSaved(false);
            }
        }
    );

    // Start OAuth flow mutation
    const startOAuthMutation = useMutation(
        () => {
            return fetch('/api/settings/linkedin/start-oauth', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                }
            }).then(res => {
                if (!res.ok) {
                    return res.json().then(errorData => {
                        throw new Error(errorData.message || `HTTP ${res.status}`);
                    });
                }
                return res.json();
            });
        },
        {
            onSuccess: (data) => {
                if (data.success && data.data.authUrl) {
                    setOauthInProgress(true);
                    // Open OAuth in new window
                    const oauthWindow = window.open(
                        data.data.authUrl,
                        'linkedin-oauth',
                        'width=600,height=700,scrollbars=yes,resizable=yes'
                    );

                    // Poll for completion
                    const pollForCompletion = setInterval(() => {
                        try {
                            if (oauthWindow.closed) {
                                clearInterval(pollForCompletion);
                                setOauthInProgress(false);

                                // Check if OAuth was successful
                                setTimeout(() => {
                                    refetchStatus().then(result => {
                                        if (result.data?.data?.hasPersonalToken) {
                                            setCurrentStep(STEPS.VERIFICATION);
                                            toast.success('LinkedIn authorization successful!');
                                        } else {
                                            toast.error('LinkedIn authorization was not completed. Please try again.');
                                        }
                                    });
                                }, 2000); // Wait 2 seconds for backend to process
                            }
                        } catch (e) {
                            // Cross-origin error, continue polling
                        }
                    }, 1000);
                } else {
                    throw new Error(data.message || 'Failed to get authorization URL');
                }
            },
            onError: (error) => {
                console.error('❌ Failed to start OAuth flow:', error);
                toast.error(`Failed to start OAuth: ${error.message}`);
                setOauthInProgress(false);
            }
        }
    );

    // Start Company OAuth flow mutation
    const startCompanyOAuthMutation = useMutation(
        () => {
            return fetch('/api/settings/linkedin/start-company-oauth', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                }
            }).then(res => {
                if (!res.ok) {
                    return res.json().then(errorData => {
                        throw new Error(errorData.message || `HTTP ${res.status}`);
                    });
                }
                return res.json();
            });
        },
        {
            onSuccess: (data) => {
                if (data.success && data.data.authUrl) {
                    setCompanyOauthInProgress(true);
                    // Open OAuth in new window
                    const oauthWindow = window.open(
                        data.data.authUrl,
                        'linkedin-company-oauth',
                        'width=600,height=700,scrollbars=yes,resizable=yes'
                    );

                    // Poll for completion
                    const pollForCompletion = setInterval(() => {
                        try {
                            if (oauthWindow.closed) {
                                clearInterval(pollForCompletion);
                                setCompanyOauthInProgress(false);

                                // Check if OAuth was successful
                                setTimeout(() => {
                                    refetchStatus().then(result => {
                                        if (result.data?.data?.hasOrganizationUrn) {
                                            setCurrentStep(STEPS.COMPANY_VERIFICATION);
                                            toast.success('LinkedIn company authorization successful!');
                                        } else if (result.data?.data?.hasOrganizationToken) {
                                            setCurrentStep(STEPS.MANUAL_URN_ENTRY);
                                            toast.success('LinkedIn company authorization successful! Please enter your organization URN.');
                                        } else {
                                            toast.error('LinkedIn company authorization was not completed. Please try again.');
                                        }
                                    });
                                }, 2000); // Wait 2 seconds for backend to process
                            }
                        } catch (e) {
                            // Cross-origin error, continue polling
                        }
                    }, 1000);
                } else {
                    throw new Error(data.message || 'Failed to get company authorization URL');
                }
            },
            onError: (error) => {
                console.error('❌ Failed to start company OAuth flow:', error);
                toast.error(`Failed to start company OAuth: ${error.message}`);
                setCompanyOauthInProgress(false);
            }
        }
    );

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard!');
    };

    const validateAppCredentials = () => {
        if (!appCredentials.clientId.trim()) {
            toast.error('Please enter your LinkedIn Client ID');
            return false;
        }

        if (!appCredentials.clientSecret.trim()) {
            toast.error('Please enter your LinkedIn Client Secret');
            return false;
        }

        if (appCredentials.clientId.length < 8) {
            toast.error('Client ID appears too short. Please check it\'s complete.');
            return false;
        }

        if (appCredentials.clientSecret.length < 16) {
            toast.error('Client Secret appears too short. Please check it\'s complete.');
            return false;
        }

        return true;
    };

    const handleNext = () => {
        switch (currentStep) {
            case STEPS.WELCOME:
                setCurrentStep(STEPS.CREATE_APP);
                break;

            case STEPS.CREATE_APP:
                setCurrentStep(STEPS.APP_CREDENTIALS);
                break;

            case STEPS.APP_CREDENTIALS:
                if (validateAppCredentials()) {
                    saveAppCredentialsMutation.mutate(appCredentials);
                }
                break;

            case STEPS.OAUTH_FLOW:
                // Check if credentials are saved before starting OAuth
                if (!linkedinStatus?.data?.hasAppCredentials && !credentialsSaved) {
                    toast.error('Please save your app credentials first');
                    setCurrentStep(STEPS.APP_CREDENTIALS);
                    return;
                }
                startOAuthMutation.mutate();
                break;

            case STEPS.VERIFICATION:
                setCurrentStep(STEPS.COMPANY_SETUP_CHOICE);
                break;

            case STEPS.COMPANY_SETUP_CHOICE:
                if (wantsCompanySetup === true) {
                    setCurrentStep(STEPS.ADVERTISING_API_REQUEST);
                } else {
                    setCurrentStep(STEPS.COMPLETE);
                }
                break;

            case STEPS.ADVERTISING_API_REQUEST:
                setCurrentStep(STEPS.COMPANY_OAUTH);
                break;

            case STEPS.COMPANY_OAUTH:
                startCompanyOAuthMutation.mutate();
                break;

            case STEPS.MANUAL_URN_ENTRY:
                if (manualOrganizationUrn.trim()) {
                    saveManualUrnMutation.mutate(manualOrganizationUrn.trim());
                } else {
                    toast.error('Please enter your organization URN');
                }
                break;

            case STEPS.COMPANY_VERIFICATION:
                setCurrentStep(STEPS.COMPLETE);
                break;

            case STEPS.COMPLETE:
                onComplete?.();
                break;
        }
    };

    const handleBack = () => {
        // Allow manual navigation back to any previous step
        switch (currentStep) {
            case STEPS.CREATE_APP:
                setCurrentStep(STEPS.WELCOME);
                break;
            case STEPS.APP_CREDENTIALS:
                setCurrentStep(STEPS.CREATE_APP);
                break;
            case STEPS.OAUTH_FLOW:
                setCurrentStep(STEPS.APP_CREDENTIALS);
                break;
            case STEPS.VERIFICATION:
                setCurrentStep(STEPS.OAUTH_FLOW);
                break;
            case STEPS.COMPANY_SETUP_CHOICE:
                setCurrentStep(STEPS.VERIFICATION);
                break;
            case STEPS.ADVERTISING_API_REQUEST:
                setCurrentStep(STEPS.COMPANY_SETUP_CHOICE);
                break;
            case STEPS.COMPANY_OAUTH:
                setCurrentStep(STEPS.ADVERTISING_API_REQUEST);
                break;
            case STEPS.MANUAL_URN_ENTRY:
                setCurrentStep(STEPS.COMPANY_OAUTH);
                break;
            case STEPS.COMPANY_VERIFICATION:
                setCurrentStep(STEPS.MANUAL_URN_ENTRY);
                break;
            case STEPS.COMPLETE:
                // Allow going back from complete to modify setup
                if (wantsCompanySetup === true && linkedinStatus?.data?.hasOrganizationUrn) {
                    setCurrentStep(STEPS.COMPANY_VERIFICATION);
                } else if (wantsCompanySetup === true && linkedinStatus?.data?.hasOrganizationToken) {
                    setCurrentStep(STEPS.MANUAL_URN_ENTRY);
                } else if (wantsCompanySetup === true) {
                    setCurrentStep(STEPS.COMPANY_SETUP_CHOICE);
                } else {
                    setCurrentStep(STEPS.VERIFICATION);
                }
                break;
        }
    };

    const getStepNumber = (step) => {
        const steps = Object.values(STEPS);
        return steps.indexOf(step) + 1;
    };

    const isStepComplete = (step) => {
        const status = linkedinStatus?.data;
        if (!status) return false;

        switch (step) {
            case STEPS.WELCOME:
                return true;
            case STEPS.CREATE_APP:
                return true; // Assume user can create app
            case STEPS.APP_CREDENTIALS:
                return status.hasAppCredentials || credentialsSaved;
            case STEPS.OAUTH_FLOW:
                return status.hasPersonalToken;
            case STEPS.VERIFICATION:
                return status.hasPersonalUrn;
            case STEPS.COMPANY_SETUP_CHOICE:
                return wantsCompanySetup !== null;
            case STEPS.ADVERTISING_API_REQUEST:
                return advertisingApiRequested || status.hasOrganizationToken;
            case STEPS.COMPANY_OAUTH:
                return status.hasOrganizationToken;
            case STEPS.MANUAL_URN_ENTRY:
                return status.hasOrganizationUrn;
            case STEPS.COMPANY_VERIFICATION:
                return status.hasOrganizationUrn;
            case STEPS.COMPLETE:
                return status.isFullyConfigured;
            default:
                return false;
        }
    };

    const getCurrentBaseUrl = () => {
        return `${window.location.protocol}//${window.location.host}`;
    };

    const canNavigateToStep = (step) => {
        // Users can navigate to any completed step or the current step
        return isStepComplete(step) || step === currentStep;
    };

    const handleStepClick = (step) => {
        if (canNavigateToStep(step)) {
            setCurrentStep(step);
        }
    };

    const canProceedToNext = () => {
        switch (currentStep) {
            case STEPS.APP_CREDENTIALS:
                return appCredentials.clientId.trim() && appCredentials.clientSecret.trim() && !saveAppCredentialsMutation.isLoading;
            case STEPS.OAUTH_FLOW:
                return (linkedinStatus?.data?.hasAppCredentials || credentialsSaved) && !oauthInProgress;
            case STEPS.COMPANY_SETUP_CHOICE:
                return wantsCompanySetup !== null;
            case STEPS.COMPANY_OAUTH:
                return !companyOauthInProgress;
            case STEPS.MANUAL_URN_ENTRY:
                return manualOrganizationUrn.trim() && !saveManualUrnMutation.isLoading;
            default:
                return true;
        }
    };

    // Debug information component
    const DebugInfo = () => {
        if (process.env.NODE_ENV !== 'development') return null;

        return (
            <div className="mt-4 p-3 bg-gray-100 rounded text-xs">
                <strong>Debug Info:</strong>
                <div className="mt-1 space-y-1">
                    <div>Current Step: {currentStep}</div>
                    <div>Status Loading: {statusLoading.toString()}</div>
                    <div>Has App Credentials: {linkedinStatus?.data?.hasAppCredentials?.toString() || 'false'}</div>
                    <div>Has Personal Token: {linkedinStatus?.data?.hasPersonalToken?.toString() || 'false'}</div>
                    <div>Has Personal URN: {linkedinStatus?.data?.hasPersonalUrn?.toString() || 'false'}</div>
                    <div>Has Organization Token: {linkedinStatus?.data?.hasOrganizationToken?.toString() || 'false'}</div>
                    <div>Has Organization URN: {linkedinStatus?.data?.hasOrganizationUrn?.toString() || 'false'}</div>
                    <div>Organization API Pending: {linkedinStatus?.data?.organizationApiPending?.toString() || 'false'}</div>
                    <div>Credentials Saved: {credentialsSaved.toString()}</div>
                    <div>Wants Company Setup: {wantsCompanySetup?.toString() || 'null'}</div>
                    <div>Advertising API Requested: {advertisingApiRequested.toString()}</div>
                    <div>Manual Org URN Length: {manualOrganizationUrn.length}</div>
                    <div>Client ID Length: {appCredentials.clientId.length}</div>
                    <div>Client Secret Length: {appCredentials.clientSecret.length}</div>
                </div>
            </div>
        );
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case STEPS.WELCOME:
                return (
                    <div className="space-y-6">
                        <div className="text-center">
                            <div className="text-6xl mb-4">💼</div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                Connect Your LinkedIn Account
                            </h2>
                            <p className="text-gray-600 max-w-md mx-auto">
                                We'll walk you through setting up LinkedIn posting in just a few steps.
                                You'll need to create your own LinkedIn developer app for secure access.
                            </p>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <InformationCircleIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-3" />
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium mb-2">What you'll need:</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>A LinkedIn account (personal or business)</li>
                                        <li>5-10 minutes to complete the setup</li>
                                        <li>Access to LinkedIn Developer Portal</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <h3 className="font-medium text-gray-900">Setup Overview:</h3>
                            <div className="space-y-2">
                                {[
                                    'Create LinkedIn Developer App',
                                    'Configure App Credentials',
                                    'Authorize Personal Access',
                                    'Verify Personal Connection',
                                    'Choose Company Setup (Optional)',
                                    'Request Advertising API (If Company)',
                                    'Authorize Company Access (If Company)',
                                    'Enter Organization URN (If Needed)',
                                    'Start Posting!'
                                ].map((step, index) => (
                                    <div key={index} className="flex items-center text-sm text-gray-600">
                                        <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center mr-3 text-xs font-medium">
                                            {index + 1}
                                        </div>
                                        {step}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <DebugInfo />
                    </div>
                );

            case STEPS.MANUAL_URN_ENTRY:
                return (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Enter Organization URN
                            </h2>
                            <p className="text-gray-600">
                                We couldn't automatically detect your company pages due to pending API approval.
                                Please manually enter your LinkedIn organization URN to complete the setup.
                            </p>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <InformationCircleIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-3" />
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium mb-1">How to find your Organization URN:</p>
                                    <ol className="list-decimal list-inside space-y-1">
                                        <li>Go to your LinkedIn company page</li>
                                        <li>Look at the URL: linkedin.com/company/[company-name]/</li>
                                        <li>The URN format is: <code>urn:li:organization:[NUMERIC_ID]</code></li>
                                        <li>If your URL shows a name, you may need to inspect the page source to find the numeric ID</li>
                                    </ol>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label htmlFor="organizationUrn" className="block text-sm font-medium text-gray-700 mb-1">
                                    Organization URN
                                </label>
                                <input
                                    type="text"
                                    id="organizationUrn"
                                    value={manualOrganizationUrn}
                                    onChange={(e) => {
                                        let value = e.target.value.trim();

                                        // If it's just numbers, automatically prepend the URN prefix
                                        if (/^\d+$/.test(value)) {
                                            value = `urn:li:organization:${value}`;
                                        }
                                        // If it starts with just the organization ID after urn:li:organization:, keep it as is
                                        // If it's a partial URN being typed, don't interfere

                                        setManualOrganizationUrn(value);
                                    }}
                                    onPaste={(e) => {
                                        // Handle paste events specifically for better UX
                                        e.preventDefault();
                                        const pastedText = e.clipboardData.getData('text').trim();

                                        let formattedValue = pastedText;

                                        // If pasted content is just numbers, add the URN prefix
                                        if (/^\d+$/.test(pastedText)) {
                                            formattedValue = `urn:li:organization:${pastedText}`;
                                        }
                                        // If pasted content already has the prefix, use as is
                                        else if (pastedText.startsWith('urn:li:organization:')) {
                                            formattedValue = pastedText;
                                        }
                                        // If it's a LinkedIn company URL, try to extract the ID
                                        else if (pastedText.includes('linkedin.com/company/')) {
                                            // Handle cases like https://www.linkedin.com/company/123456/
                                            const urlMatch = pastedText.match(/linkedin\.com\/company\/(\d+)/);
                                            if (urlMatch && urlMatch[1]) {
                                                formattedValue = `urn:li:organization:${urlMatch[1]}`;
                                            } else {
                                                // If it's a company URL but no numeric ID found, use as pasted
                                                formattedValue = pastedText;
                                            }
                                        }

                                        setManualOrganizationUrn(formattedValue);
                                    }}
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="urn:li:organization:12345678 or just 12345678"
                                    disabled={saveManualUrnMutation.isLoading}
                                />
                                <p className="mt-1 text-sm text-gray-500">
                                    Paste just the numeric ID (e.g., 12345678) or the full URN (urn:li:organization:12345678)
                                </p>
                            </div>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <CheckCircleIcon className="h-5 w-5 text-green-400 mt-0.5 mr-3" />
                                <div className="text-sm text-green-800">
                                    <p className="font-medium mb-1">Good news:</p>
                                    <p>
                                        Once you enter the URN and LinkedIn approves your API access,
                                        company posting will work automatically without any additional setup.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <DebugInfo />
                    </div>
                );

            case STEPS.CREATE_APP:
                return (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Create LinkedIn Developer App
                            </h2>
                            <p className="text-gray-600">
                                First, you need to create a LinkedIn app to get API access. Don't worry - it's free and takes just a few minutes.
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                                        <span className="text-blue-600 font-medium text-sm">1</span>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-medium text-gray-900 mb-2">
                                            Go to LinkedIn Developer Portal
                                        </h3>
                                        <p className="text-sm text-gray-600 mb-3">
                                            Open the LinkedIn Developer Portal and sign in with your LinkedIn account.
                                        </p>
                                        <a
                                            href="https://developer.linkedin.com/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                        >
                                            Open Developer Portal
                                            <LinkIcon className="ml-2 h-4 w-4" />
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                                        <span className="text-blue-600 font-medium text-sm">2</span>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-medium text-gray-900 mb-2">
                                            Create New App
                                        </h3>
                                        <p className="text-sm text-gray-600 mb-3">
                                            Click "Create App" and fill in the required information:
                                        </p>
                                        <div className="bg-gray-50 p-3 rounded text-sm space-y-2">
                                            <div><strong>App name:</strong> PostMgr Integration</div>
                                            <div><strong>LinkedIn Page:</strong> Select your personal profile or company page</div>
                                            <div><strong>App description:</strong> Social media management integration</div>
                                            <div><strong>App logo:</strong> Optional (you can skip this)</div>
                                            <div><strong>Privacy Policy:</strong>https://postmgr.com/privacy</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                                        <span className="text-blue-600 font-medium text-sm">3</span>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-medium text-gray-900 mb-2">
                                            Configure OAuth Settings
                                        </h3>
                                        <p className="text-sm text-gray-600 mb-3">
                                            In your app settings, go to the "Auth" tab and add these redirect URLs:
                                        </p>
                                        <div className="space-y-2">
                                            <div className="bg-gray-50 p-3 rounded border">
                                                <div className="flex items-center justify-between">
                                                    <code className="text-sm font-mono text-gray-800">
                                                        {getCurrentBaseUrl()}/auth/linkedin/callback
                                                    </code>
                                                    <button
                                                        onClick={() => copyToClipboard(`${getCurrentBaseUrl()}/auth/linkedin/callback`)}
                                                        className="ml-2 p-1 text-gray-500 hover:text-gray-700"
                                                        title="Copy to clipboard"
                                                    >
                                                        <ClipboardDocumentIcon className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="bg-gray-50 p-3 rounded border">
                                                <div className="flex items-center justify-between">
                                                    <code className="text-sm font-mono text-gray-800">
                                                        {getCurrentBaseUrl()}/auth/linkedin-company/callback
                                                    </code>
                                                    <button
                                                        onClick={() => copyToClipboard(`${getCurrentBaseUrl()}/auth/linkedin-company/callback`)}
                                                        className="ml-2 p-1 text-gray-500 hover:text-gray-700"
                                                        title="Copy to clipboard"
                                                    >
                                                        <ClipboardDocumentIcon className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                                        <span className="text-blue-600 font-medium text-sm">4</span>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-medium text-gray-900 mb-2">
                                            Request Permissions
                                        </h3>
                                        <p className="text-sm text-gray-600 mb-3">
                                            In the "Products" tab, request these permissions:
                                        </p>
                                        <ul className="text-sm text-gray-600 space-y-1">
                                            <li>• <strong>Sign In with LinkedIn using OpenID Connect</strong> (usually auto-approved)</li>
                                            <li>• <strong>Share on LinkedIn</strong> (may require review)</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mt-0.5 mr-3" />
                                <div className="text-sm text-yellow-800">
                                    <p className="font-medium mb-1">Note about approval:</p>
                                    <p>
                                        LinkedIn may require review for the "Share on LinkedIn" permission.
                                        This can take 1-7 days. You can continue setup and posting will work once approved.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <DebugInfo />
                    </div>
                );

            case STEPS.APP_CREDENTIALS:
                return (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Enter Your App Credentials
                            </h2>
                            <p className="text-gray-600">
                                Copy your Client ID and Client Secret from your LinkedIn app's Auth tab.
                            </p>
                        </div>

                        {/* Status indicator */}
                        {(linkedinStatus?.data?.hasAppCredentials || credentialsSaved) && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <div className="flex items-center">
                                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />
                                    <span className="text-sm font-medium text-green-800">
                                        App credentials have been saved successfully!
                                    </span>
                                </div>
                            </div>
                        )}

                        <div className="space-y-4">
                            <div>
                                <label htmlFor="clientId" className="block text-sm font-medium text-gray-700 mb-1">
                                    Client ID
                                </label>
                                <input
                                    type="text"
                                    id="clientId"
                                    value={appCredentials.clientId}
                                    onChange={(e) => setAppCredentials(prev => ({ ...prev, clientId: e.target.value.trim() }))}
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Enter your LinkedIn app Client ID"
                                    disabled={saveAppCredentialsMutation.isLoading}
                                />
                                <p className="mt-1 text-sm text-gray-500">
                                    Found in your LinkedIn app's "Auth" tab
                                </p>
                            </div>

                            <div>
                                <label htmlFor="clientSecret" className="block text-sm font-medium text-gray-700 mb-1">
                                    Client Secret
                                </label>
                                <div className="relative">
                                    <input
                                        type={showClientSecret ? 'text' : 'password'}
                                        id="clientSecret"
                                        value={appCredentials.clientSecret}
                                        onChange={(e) => setAppCredentials(prev => ({ ...prev, clientSecret: e.target.value.trim() }))}
                                        className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Enter your LinkedIn app Client Secret"
                                        disabled={saveAppCredentialsMutation.isLoading}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowClientSecret(!showClientSecret)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    >
                                        {showClientSecret ? (
                                            <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                                        ) : (
                                            <EyeIcon className="h-5 w-5 text-gray-400" />
                                        )}
                                    </button>
                                </div>
                                <p className="mt-1 text-sm text-gray-500">
                                    Found in your LinkedIn app's "Auth" tab (click "Show" to reveal)
                                </p>
                            </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <InformationCircleIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-3" />
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium mb-1">Where to find these:</p>
                                    <ol className="list-decimal list-inside space-y-1">
                                        <li>Go to your LinkedIn app in the Developer Portal</li>
                                        <li>Click on the "Auth" tab</li>
                                        <li>Your Client ID is displayed at the top</li>
                                        <li>Click "Show" next to Client Secret to reveal it</li>
                                    </ol>
                                </div>
                            </div>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <CheckCircleIcon className="h-5 w-5 text-green-400 mt-0.5 mr-3" />
                                <div className="text-sm text-green-800">
                                    <p className="font-medium mb-1">Security Note:</p>
                                    <p>
                                        Your credentials are encrypted and stored securely. They're only used to
                                        authenticate with LinkedIn on your behalf.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <DebugInfo />
                    </div>
                );

            case STEPS.OAUTH_FLOW:
                return (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Authorize PostMgr Access
                            </h2>
                            <p className="text-gray-600">
                                Now we'll connect PostMgr to your LinkedIn account using the secure OAuth flow.
                            </p>
                        </div>

                        {/* Prerequisites check */}
                        {!linkedinStatus?.data?.hasAppCredentials && !credentialsSaved && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <ExclamationCircleIcon className="h-5 w-5 text-red-400 mt-0.5 mr-3" />
                                    <div className="text-sm text-red-800">
                                        <p className="font-medium mb-1">Prerequisites Missing:</p>
                                        <p>You need to save your LinkedIn app credentials before starting OAuth authorization.</p>
                                        <button
                                            onClick={() => setCurrentStep(STEPS.APP_CREDENTIALS)}
                                            className="mt-2 text-sm underline text-red-600 hover:text-red-700"
                                        >
                                            Go back to App Credentials
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                            <div className="text-4xl mb-4">🔗</div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                Ready to Connect
                            </h3>
                            <p className="text-gray-600 mb-6">
                                Click the button below to open LinkedIn's authorization page.
                                You'll be asked to sign in and grant PostMgr permission to post on your behalf.
                            </p>

                            {oauthInProgress ? (
                                <div className="space-y-3">
                                    <div className="animate-spin mx-auto h-8 w-8 border-b-2 border-blue-600 rounded-full"></div>
                                    <p className="text-sm text-gray-600">
                                        Please complete the authorization in the popup window...
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        If the popup was blocked, please allow popups for this site and try again.
                                    </p>
                                </div>
                            ) : (
                                <button
                                    onClick={() => startOAuthMutation.mutate()}
                                    disabled={startOAuthMutation.isLoading || !canProceedToNext()}
                                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {startOAuthMutation.isLoading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                            Starting...
                                        </>
                                    ) : (
                                        <>
                                            Authorize LinkedIn Access
                                            <LinkIcon className="ml-2 h-5 w-5" />
                                        </>
                                    )}
                                </button>
                            )}
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <InformationCircleIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-3" />
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium mb-1">What happens next:</p>
                                    <ol className="list-decimal list-inside space-y-1">
                                        <li>A popup window will open with LinkedIn's authorization page</li>
                                        <li>Sign in to LinkedIn if prompted</li>
                                        <li>Review and accept the permissions</li>
                                        <li>The popup will close automatically</li>
                                        <li>We'll verify the connection and get your profile information</li>
                                    </ol>
                                </div>
                            </div>
                        </div>

                        <DebugInfo />
                    </div>
                );

            case STEPS.VERIFICATION:
                return (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Verify Your Connection
                            </h2>
                            <p className="text-gray-600">
                                Great! We've received your authorization. Let's verify everything is working correctly.
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />
                                        <span className="text-sm font-medium text-gray-900">
                                            LinkedIn Authorization
                                        </span>
                                    </div>
                                    <span className="text-sm text-green-600">Connected</span>
                                </div>
                            </div>

                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        {linkedinStatus?.data?.hasPersonalUrn ? (
                                            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />
                                        ) : (
                                            <div className="animate-spin h-5 w-5 border-b-2 border-blue-600 rounded-full mr-3"></div>
                                        )}
                                        <span className="text-sm font-medium text-gray-900">
                                            Profile Information
                                        </span>
                                    </div>
                                    <span className={`text-sm ${linkedinStatus?.data?.hasPersonalUrn ? 'text-green-600' : 'text-yellow-600'}`}>
                                        {linkedinStatus?.data?.hasPersonalUrn ? 'Retrieved' : 'Getting...'}
                                    </span>
                                </div>
                            </div>

                            {linkedinStatus?.data?.profileInfo && (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                    <div className="flex items-start">
                                        <CheckCircleIcon className="h-5 w-5 text-green-400 mt-0.5 mr-3" />
                                        <div className="text-sm text-green-800">
                                            <p className="font-medium mb-1">Profile Connected:</p>
                                            <p>
                                                {linkedinStatus.data.profileInfo.name} ({linkedinStatus.data.profileInfo.email})
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="text-center">
                            <div className="text-4xl mb-4">🎉</div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                Personal Setup Complete!
                            </h3>
                            <p className="text-gray-600">
                                Your personal LinkedIn account is successfully connected. Next, you can choose whether to also set up company page posting.
                            </p>
                        </div>

                        <DebugInfo />
                    </div>
                );

            case STEPS.COMPANY_SETUP_CHOICE:
                return (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Company Page Setup
                            </h2>
                            <p className="text-gray-600">
                                Your personal LinkedIn account is now connected! Do you also want to set up posting to a LinkedIn company page?
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <button
                                onClick={() => setWantsCompanySetup(true)}
                                className={`p-6 border-2 rounded-lg text-left transition-all ${
                                    wantsCompanySetup === true
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                }`}
                            >
                                <div className="text-3xl mb-3">🏢</div>
                                <h3 className="font-medium text-gray-900 mb-2">Yes, set up company posting</h3>
                                <p className="text-sm text-gray-600">
                                    I want to post to a LinkedIn company page in addition to my personal profile.
                                </p>
                                <div className="mt-3 text-xs text-gray-500">
                                    • Requires LinkedIn Advertising API access
                                    • Need admin access to company page
                                    • Additional approval process (~1-7 days)
                                </div>
                            </button>

                            <button
                                onClick={() => setWantsCompanySetup(false)}
                                className={`p-6 border-2 rounded-lg text-left transition-all ${
                                    wantsCompanySetup === false
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                }`}
                            >
                                <div className="text-3xl mb-3">👤</div>
                                <h3 className="font-medium text-gray-900 mb-2">No, personal only</h3>
                                <p className="text-sm text-gray-600">
                                    I only want to post to my personal LinkedIn profile for now.
                                </p>
                                <div className="mt-3 text-xs text-gray-500">
                                    • Ready to use immediately
                                    • Can add company posting later
                                    • Complete setup now
                                </div>
                            </button>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <InformationCircleIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-3" />
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium mb-1">Company posting requirements:</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>Admin access to a LinkedIn company page</li>
                                        <li>LinkedIn Advertising API approval (may take 1-7 days)</li>
                                        <li>Additional OAuth authorization for company access</li>
                                    </ul>
                                    <p className="mt-2">You can always add company posting later if you skip it now.</p>
                                </div>
                            </div>
                        </div>

                        <DebugInfo />
                    </div>
                );

            case STEPS.ADVERTISING_API_REQUEST:
                return (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Request LinkedIn Advertising API Access
                            </h2>
                            <p className="text-gray-600">
                                To post to company pages, you need access to LinkedIn's Advertising API. This requires filling out an application form.
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                                        <span className="text-blue-600 font-medium text-sm">1</span>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-medium text-gray-900 mb-2">
                                            Go to LinkedIn Marketing Developer Platform
                                        </h3>
                                        <p className="text-sm text-gray-600 mb-3">
                                            Open the Marketing Developer Platform and click "Apply for Access"
                                        </p>
                                        <a
                                            href="https://business.linkedin.com/marketing-solutions/marketing-partners/become-a-partner"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                        >
                                            Apply for Advertising API
                                            <LinkIcon className="ml-2 h-4 w-4" />
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                                        <span className="text-blue-600 font-medium text-sm">2</span>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-medium text-gray-900 mb-2">
                                            Fill Out the Application Form
                                        </h3>
                                        <p className="text-sm text-gray-600 mb-3">
                                            Complete the partner application with these details:
                                        </p>
                                        <div className="bg-gray-50 p-3 rounded text-sm space-y-2">
                                            <div><strong>Application Type:</strong> Technology Partner</div>
                                            <div><strong>Use Case:</strong> Social Media Management Platform</div>
                                            <div><strong>Products Needed:</strong> Advertising API</div>
                                            <div><strong>Company Description:</strong> Social media content management and scheduling tool</div>
                                            <div><strong>Integration Purpose:</strong> Allow users to post content to LinkedIn company pages through our platform</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                                        <span className="text-blue-600 font-medium text-sm">3</span>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-medium text-gray-900 mb-2">
                                            Wait for Approval
                                        </h3>
                                        <p className="text-sm text-gray-600 mb-3">
                                            LinkedIn will review your application and notify you via email. This typically takes 1-7 business days.
                                        </p>
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id="apiRequested"
                                                checked={advertisingApiRequested}
                                                onChange={(e) => setAdvertisingApiRequested(e.target.checked)}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            />
                                            <label htmlFor="apiRequested" className="ml-2 text-sm text-gray-700">
                                                I have submitted the API access request
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mt-0.5 mr-3" />
                                <div className="text-sm text-yellow-800">
                                    <p className="font-medium mb-1">Important Notes:</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>You must be an admin of the LinkedIn company page you want to post to</li>
                                        <li>The application may ask for proof of your business/platform</li>
                                        <li>You can continue with the setup process even while waiting for approval</li>
                                        <li>Company posting will only work after approval is granted</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <DebugInfo />
                    </div>
                );

            case STEPS.COMPANY_OAUTH:
                return (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Authorize Company Access
                            </h2>
                            <p className="text-gray-600">
                                Now we'll get authorization to post to your LinkedIn company pages. This uses a separate OAuth flow with company-specific permissions.
                            </p>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                            <div className="text-4xl mb-4">🏢</div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                Ready to Connect Company Access
                            </h3>
                            <p className="text-gray-600 mb-6">
                                This will open LinkedIn's authorization page where you'll grant PostMgr permission to post on behalf of your company pages.
                                Make sure you're logged into LinkedIn as an admin of the company page.
                            </p>

                            {companyOauthInProgress ? (
                                <div className="space-y-3">
                                    <div className="animate-spin mx-auto h-8 w-8 border-b-2 border-blue-600 rounded-full"></div>
                                    <p className="text-sm text-gray-600">
                                        Please complete the company authorization in the popup window...
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        You may need to select which company pages to authorize.
                                    </p>
                                </div>
                            ) : (
                                <button
                                    onClick={() => startCompanyOAuthMutation.mutate()}
                                    disabled={startCompanyOAuthMutation.isLoading || !canProceedToNext()}
                                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {startCompanyOAuthMutation.isLoading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                            Starting...
                                        </>
                                    ) : (
                                        <>
                                            Authorize Company Access
                                            <LinkIcon className="ml-2 h-5 w-5" />
                                        </>
                                    )}
                                </button>
                            )}
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <InformationCircleIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-3" />
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium mb-1">What happens next:</p>
                                    <ol className="list-decimal list-inside space-y-1">
                                        <li>A popup window will open with LinkedIn's company authorization page</li>
                                        <li>Sign in to LinkedIn if prompted (use admin account)</li>
                                        <li>Select which company pages to authorize for PostMgr</li>
                                        <li>Review and accept the company permissions</li>
                                        <li>The popup will close and we'll fetch your company page information</li>
                                    </ol>
                                </div>
                            </div>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mt-0.5 mr-3" />
                                <div className="text-sm text-yellow-800">
                                    <p className="font-medium mb-1">Prerequisites:</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>You must have admin access to at least one LinkedIn company page</li>
                                        <li>Your LinkedIn app should have Advertising API approval (or be pending)</li>
                                        <li>You must be logged into LinkedIn as the admin account</li>
                                    </ul>
                                    <p className="mt-2">
                                        <strong>Note:</strong> If API approval is pending, you'll need to manually enter your organization URN in the next step.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <DebugInfo />
                    </div>
                );

            case STEPS.COMPANY_VERIFICATION:
                return (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Verify Company Connection
                            </h2>
                            <p className="text-gray-600">
                                Excellent! We've received your company authorization. Let's verify everything is working correctly.
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />
                                        <span className="text-sm font-medium text-gray-900">
                                            Company Authorization
                                        </span>
                                    </div>
                                    <span className="text-sm text-green-600">Connected</span>
                                </div>
                            </div>

                            <div className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        {linkedinStatus?.data?.hasOrganizationUrn ? (
                                            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />
                                        ) : linkedinStatus?.data?.organizationApiPending ? (
                                            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mr-3" />
                                        ) : (
                                            <div className="animate-spin h-5 w-5 border-b-2 border-blue-600 rounded-full mr-3"></div>
                                        )}
                                        <span className="text-sm font-medium text-gray-900">
                                            Company Pages Information
                                        </span>
                                    </div>
                                    <span className={`text-sm ${
                                        linkedinStatus?.data?.hasOrganizationUrn
                                            ? 'text-green-600'
                                            : linkedinStatus?.data?.organizationApiPending
                                                ? 'text-yellow-600'
                                                : 'text-yellow-600'
                                    }`}>
                                        {linkedinStatus?.data?.hasOrganizationUrn
                                            ? 'Retrieved'
                                            : linkedinStatus?.data?.organizationApiPending
                                                ? 'API Pending'
                                                : 'Getting...'}
                                    </span>
                                </div>
                            </div>

                            {/* Show different content based on API approval status */}
                            {linkedinStatus?.data?.organizationApiPending ? (
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                    <div className="flex items-start">
                                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mt-0.5 mr-3" />
                                        <div className="text-sm text-yellow-800">
                                            <p className="font-medium mb-1">⏳ LinkedIn Advertising API Approval Pending</p>
                                            <p className="mb-2">
                                                Your company authorization was successful, but LinkedIn's Advertising API approval is still pending.
                                            </p>
                                            <ul className="list-disc list-inside space-y-1">
                                                <li>This is normal and usually takes 1-7 business days</li>
                                                <li>You'll receive an email from LinkedIn when approved</li>
                                                <li>Company posting will automatically work once approved</li>
                                                <li>Personal posting is already working</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            ) : linkedinStatus?.data?.organizationInfo && Array.isArray(linkedinStatus.data.organizationInfo) ? (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                    <div className="flex items-start">
                                        <CheckCircleIcon className="h-5 w-5 text-green-400 mt-0.5 mr-3" />
                                        <div className="text-sm text-green-800">
                                            <p className="font-medium mb-1">Company Pages Connected:</p>
                                            <ul className="list-disc list-inside space-y-1">
                                                {linkedinStatus.data.organizationInfo.map((org, index) => (
                                                    <li key={index}>{org.name}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            ) : linkedinStatus?.data?.organizationInfo && !Array.isArray(linkedinStatus.data.organizationInfo) ? (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                    <div className="flex items-start">
                                        <CheckCircleIcon className="h-5 w-5 text-green-400 mt-0.5 mr-3" />
                                        <div className="text-sm text-green-800">
                                            <p className="font-medium mb-1">Company Page Connected:</p>
                                            <p>{linkedinStatus.data.organizationInfo.name}</p>
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        <div className="text-center">
                            <div className="text-4xl mb-4">
                                {linkedinStatus?.data?.organizationApiPending ? '⏳' : '🎉'}
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                {linkedinStatus?.data?.organizationApiPending
                                    ? 'Company Setup In Progress!'
                                    : 'Company Setup Complete!'}
                            </h3>
                            <p className="text-gray-600">
                                {linkedinStatus?.data?.organizationApiPending
                                    ? 'Your company authorization is complete. Company posting will be enabled once LinkedIn approves your API access.'
                                    : 'Your LinkedIn company pages are successfully connected. You can now post to both your personal profile and company pages from PostMgr.'}
                            </p>
                        </div>

                        <DebugInfo />
                    </div>
                );

            case STEPS.COMPLETE:
                return (
                    <div className="space-y-6 text-center">
                        <div className="text-6xl mb-4">✨</div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            LinkedIn Integration Complete!
                        </h2>
                        <p className="text-gray-600 max-w-md mx-auto">
                            Your LinkedIn account is now fully connected and ready for posting.
                            {wantsCompanySetup === true && linkedinStatus?.data?.hasOrganizationUrn
                                ? ' Both personal and company posting are enabled.'
                                : ' Personal posting is enabled.'}
                        </p>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="text-sm text-green-800 space-y-2">
                                <p className="font-medium">✅ What's now enabled:</p>
                                <ul className="list-disc list-inside space-y-1 text-left max-w-md mx-auto">
                                    <li>AI-powered post generation</li>
                                    <li>Direct posting to your LinkedIn profile</li>
                                    {wantsCompanySetup === true && linkedinStatus?.data?.hasOrganizationUrn && (
                                        <li>Direct posting to your LinkedIn company pages</li>
                                    )}
                                    <li>Post scheduling and approval workflow</li>
                                    <li>Content performance tracking</li>
                                </ul>
                            </div>
                        </div>

                        {wantsCompanySetup === true && !linkedinStatus?.data?.hasOrganizationUrn && !linkedinStatus?.data?.organizationApiPending && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <div className="text-sm text-yellow-800">
                                    <p className="font-medium mb-1">⏳ Company posting pending:</p>
                                    <p>Company posting will be enabled once your LinkedIn Advertising API access is approved and you complete the company OAuth flow.</p>
                                </div>
                            </div>
                        )}

                        {wantsCompanySetup === true && linkedinStatus?.data?.organizationApiPending && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium mb-1">⏳ Advertising API approval pending:</p>
                                    <p>Company authorization is complete! Company posting will be automatically enabled once LinkedIn approves your Advertising API access (usually 1-7 days).</p>
                                </div>
                            </div>
                        )}

                        <div className="space-y-3">
                            <button
                                onClick={() => {
                                    onComplete?.();
                                    // Navigate to post generation or close wizard
                                }}
                                className="w-full inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                            >
                                Start Creating Posts
                                <ArrowRightIcon className="ml-2 h-5 w-5" />
                            </button>

                            <button
                                onClick={onClose}
                                className="text-sm text-gray-500 hover:text-gray-700"
                            >
                                I'll set this up later
                            </button>
                        </div>

                        <DebugInfo />
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                {/* Background overlay */}
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

                {/* Modal panel */}
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                    {/* Progress bar */}
                    <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                            <h1 className="text-lg font-medium text-gray-900">LinkedIn Setup</h1>
                            <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-500">Click steps to navigate</span>
                                <button
                                    onClick={onClose}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center space-x-2">
                            {Object.values(STEPS).map((step, index) => (
                                <React.Fragment key={step}>
                                    <button
                                        onClick={() => handleStepClick(step)}
                                        disabled={!canNavigateToStep(step)}
                                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                                            currentStep === step
                                                ? 'bg-blue-600 text-white'
                                                : isStepComplete(step)
                                                    ? 'bg-green-500 text-white hover:bg-green-600 cursor-pointer'
                                                    : canNavigateToStep(step)
                                                        ? 'bg-gray-200 text-gray-600 hover:bg-gray-300 cursor-pointer'
                                                        : 'bg-gray-200 text-gray-600 cursor-not-allowed'
                                        }`}
                                        title={canNavigateToStep(step) ? `Go to step ${index + 1}` : `Step ${index + 1} not available yet`}
                                    >
                                        {isStepComplete(step) ? '✓' : index + 1}
                                    </button>
                                    {index < Object.values(STEPS).length - 1 && (
                                        <div className={`flex-1 h-1 rounded ${
                                            isStepComplete(step) ? 'bg-green-500' : 'bg-gray-200'
                                        }`} />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="px-6 py-6">
                        {renderStepContent()}
                    </div>

                    {/* Footer */}
                    <div className="bg-gray-50 px-6 py-4 flex items-center justify-between">
                        <button
                            onClick={handleBack}
                            disabled={currentStep === STEPS.WELCOME}
                            className={`inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                                currentStep === STEPS.WELCOME
                                    ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                                    : 'text-gray-700 bg-white hover:bg-gray-50'
                            }`}
                        >
                            <ArrowLeftIcon className="h-4 w-4 mr-2" />
                            Back
                        </button>

                        <div className="text-sm text-gray-500">
                            Step {getStepNumber(currentStep)} of {Object.values(STEPS).length}
                        </div>

                        <button
                            onClick={handleNext}
                            disabled={!canProceedToNext()}
                            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                                !canProceedToNext()
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                        >
                            {(saveAppCredentialsMutation.isLoading || startOAuthMutation.isLoading || startCompanyOAuthMutation.isLoading || saveManualUrnMutation.isLoading) ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Processing...
                                </>
                            ) : currentStep === STEPS.COMPLETE ? (
                                'Finish'
                            ) : (
                                <>
                                    Next
                                    <ArrowRightIcon className="h-4 w-4 ml-2" />
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LinkedInOnboardingWizard;